import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { PREF_TAG, RESERVED_TAG_PREFIX } from '@orn/global/assistant/memoryTags.js';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { ClockIcon, TrashIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AssistantMemoryScope } from '../../__generated__/globalTypes';
import AdminSelectUser, {
  type IAdminSelectUserSelection,
  useSeedSelectionFromLink,
} from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import CopyButton from '../../components/CopyButton';
import IconButton from '../../components/IconButton';
import Input from '../../components/Input';
import InspectLinkedText from '../../components/InspectLinkedText';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { assistantThreadDeepLinkPath, nextUserSearchParams } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';

import { MemoryBodyCell, MemoryBodyText } from './AssistantMemoryBody';
import { useAssistantMemoryJournal } from './AssistantMemoryJournal';
import AssistantMemoryJournalList from './AssistantMemoryJournalList';
import { useAssistantUserSelection } from './AssistantUserSelectionContext';
import { prefixReason } from './prefixReason';

import type {
  AdminDeleteAllAssistantMemoryForUserMutation,
  AdminDeleteAllAssistantMemoryForUserMutationVariables,
  AdminDeleteAssistantMemoryMutation,
  AdminDeleteAssistantMemoryMutationVariables,
  AdminGetAssistantMemoryForUserQuery,
  AdminGetAssistantMemoryForUserQueryVariables,
  AdminListUsersForAssistantMemoryQuery,
} from './__generated__/AssistantMemoryScene.gql';

const ADMIN_GET_ASSISTANT_MEMORY = gql`
  query AdminGetAssistantMemoryForUser($userId: ID!) {
    adminGetAssistantMemoryForUser(userId: $userId) {
      hasMore
      includesFamilyScope
      memories {
        id
        scope
        body
        tags
        importance
        effectiveImportance
        source
        sourceThreadId
        userId
        familyId
        createdAt
        updatedAt
      }
    }
  }
`;

const ADMIN_DELETE_ASSISTANT_MEMORY = gql`
  mutation AdminDeleteAssistantMemory($id: ID!, $scope: AssistantMemoryScope!, $reason: String!) {
    adminDeleteAssistantMemory(id: $id, scope: $scope, reason: $reason)
  }
`;

const ADMIN_DELETE_ALL_ASSISTANT_MEMORY = gql`
  mutation AdminDeleteAllAssistantMemoryForUser($userId: ID!, $reason: String!) {
    adminDeleteAllAssistantMemoryForUser(userId: $userId, reason: $reason)
  }
`;

// Same shape as the picker — re-issued via useQuery so the email-by-id
// lookup is available even before the picker mounts.
const ADMIN_LIST_USERS_FOR_ASSISTANT_MEMORY = gql`
  query AdminListUsersForAssistantMemory {
    adminListUsers {
      id
      email
    }
  }
`;

type MemoryRow = AdminGetAssistantMemoryForUserQuery['adminGetAssistantMemoryForUser']['memories'][number];

type SortKey =
  | 'scope'
  | 'importance'
  | 'effectiveImportance'
  | 'body'
  | 'tags'
  | 'source'
  | 'sourceThread'
  | 'user'
  | 'updatedAt'
  | 'createdAt';
type SortDirection = 'asc' | 'desc';

type ColumnDef = { key: SortKey; label: string; align?: 'left' | 'right' };

const COLUMNS: ColumnDef[] = [
  { key: 'scope', label: 'Scope' },
  { key: 'importance', label: 'Imp', align: 'right' },
  { key: 'effectiveImportance', label: 'Eff imp', align: 'right' },
  { key: 'body', label: 'Body' },
  { key: 'tags', label: 'Tags' },
  { key: 'source', label: 'Source' },
  { key: 'sourceThread', label: 'Thread' },
  { key: 'user', label: 'User' },
  { key: 'updatedAt', label: 'Updated' },
  { key: 'createdAt', label: 'Created' },
];

const SCOPE_BADGE: Record<AssistantMemoryScope, string> = {
  [AssistantMemoryScope.User]: 'bg-bg-secondary text-fg-primary border-border-primary',
  [AssistantMemoryScope.Family]: 'bg-bg-secondary text-fg-brand-primary border-border-brand',
};

/**
 * Memory sub-tab of the Assistant tool. Operator picks a user; the pane
 * loads every non-invalidated memory visible to that user — their
 * private user-scope rows plus, for a caregiver, their family's shared
 * family-scope rows (a loved one sees only their own) — and renders them
 * in one sortable table. Per-row delete confirms via modal; the "Delete
 * all" action wipes every visible-scope row (active + invalidated) in one
 * shot with a required reason. Deletes are hard — the row is dropped from
 * the DB rather than soft-flagged — so the operator can credibly honor a
 * "permanently forget this" request.
 */
const AssistantMemoryScene = () => {
  const { selection, setSelection } = useAssistantUserSelection();
  // A deep link lands on a fresh Assistant shell whose shared selection starts null.
  const isLinkSeedPending = useSeedSelectionFromLink(setSelection);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortKey, setSortKey] = React.useState<SortKey>('effectiveImportance');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');
  const [rowDelete, setRowDelete] = React.useState<MemoryRow | null>(null);
  const [rowHistory, setRowHistory] = React.useState<MemoryRow | null>(null);
  const [rowReason, setRowReason] = React.useState('');
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkReason, setBulkReason] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  const [expandedBodies, setExpandedBodies] = React.useState<Set<string>>(new Set());

  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();

  const userId = selection?.userId ?? null;

  const { data: usersData } = useQuery<AdminListUsersForAssistantMemoryQuery>(ADMIN_LIST_USERS_FOR_ASSISTANT_MEMORY);
  const userEmailById = React.useMemo(() => {
    const map = new Map<string, string>();
    usersData?.adminListUsers?.forEach((u) => map.set(u.id, u.email));
    return map;
  }, [usersData]);

  const [loadMemory, { data: memoryData, loading: memoryLoading, refetch: refetchMemory }] = useLazyQuery<
    AdminGetAssistantMemoryForUserQuery,
    AdminGetAssistantMemoryForUserQueryVariables
  >(ADMIN_GET_ASSISTANT_MEMORY);

  const [deleteRow] = useMutation<AdminDeleteAssistantMemoryMutation, AdminDeleteAssistantMemoryMutationVariables>(
    ADMIN_DELETE_ASSISTANT_MEMORY
  );

  const [deleteAll] = useMutation<
    AdminDeleteAllAssistantMemoryForUserMutation,
    AdminDeleteAllAssistantMemoryForUserMutationVariables
  >(ADMIN_DELETE_ALL_ASSISTANT_MEMORY);

  React.useEffect(() => {
    if (userId) {
      void loadMemory({ variables: { userId } });
    }
  }, [userId, loadMemory]);

  // Keep `?userId=` naming the user on screen, so leaving the tab — a memory
  // body's id opening in the object inspector, say — and coming back on the
  // browser's Back button lands on the same user rather than an empty picker.
  //
  // Held while the link seed is in flight: the selection is still null then, and
  // writing that would delete the very `?userId=` the seed is about to read.
  // Guarded on the current value because `setSearchParams` is memoized on the
  // query string, so an unguarded write would re-fire this effect through its own
  // dependency and replace the entry a second time.
  React.useEffect(() => {
    if (isLinkSeedPending || (searchParams.get('userId') ?? null) === userId) {
      return;
    }
    setSearchParams(nextUserSearchParams(searchParams, userId), { replace: true });
  }, [isLinkSeedPending, searchParams, setSearchParams, userId]);

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setRowDelete(null);
    setRowHistory(null);
    setRowReason('');
    setBulkDeleteOpen(false);
    setBulkReason('');
    setExpandedBodies(new Set());
  };

  const toggleBodyExpanded = (id: string) => {
    setExpandedBodies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Numeric columns default to descending (highest importance first);
    // text/date columns default to ascending on first click.
    setSortDirection(key === 'importance' || key === 'effectiveImportance' ? 'desc' : 'asc');
  };

  // Memoize so the empty-array default doesn't trigger sortedMemories
  // on every render — the eslint exhaustive-deps rule otherwise warns.
  const memories = React.useMemo(() => memoryData?.adminGetAssistantMemoryForUser?.memories ?? [], [memoryData]);
  const hasMore = memoryData?.adminGetAssistantMemoryForUser?.hasMore ?? false;
  const includesFamilyScope = memoryData?.adminGetAssistantMemoryForUser?.includesFamilyScope ?? true;
  const selectedUserEmail = selection ? (userEmailById.get(selection.userId) ?? null) : null;

  const sortedMemories = React.useMemo(() => {
    const out = [...memories];
    out.sort((a, b) => compareRows(a, b, sortKey, userEmailById));
    if (sortDirection === 'desc') {
      out.reverse();
    }
    return out;
  }, [memories, sortKey, sortDirection, userEmailById]);

  const [preferenceMemories, otherMemories] = React.useMemo(
    () => [
      sortedMemories.filter((row) => row.tags.includes(PREF_TAG)),
      sortedMemories.filter((row) => !row.tags.includes(PREF_TAG)),
    ],
    [sortedMemories]
  );

  const renderMemoryRow = (row: MemoryRow) => {
    const writer = writerLabel(row, userEmailById);
    return (
      <Table.Row key={row.id}>
        <Table.Cell>
          <span
            className={cx(
              'inline-flex items-center border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
              SCOPE_BADGE[row.scope]
            )}
          >
            {row.scope}
          </span>
        </Table.Cell>
        <Table.Cell className="text-right">{row.importance}</Table.Cell>
        {/* Server-rounded (core computeEffectiveImportance → Math.round); mirror the
            integer as-is rather than a client .toFixed that always shows a fake `.00`. */}
        <Table.Cell className="text-right">{row.effectiveImportance}</Table.Cell>
        <Table.Cell className="max-w-[480px]">
          <MemoryBodyCell
            body={row.body}
            expanded={expandedBodies.has(row.id)}
            onToggle={() => toggleBodyExpanded(row.id)}
          />
        </Table.Cell>
        <Table.Cell>
          <TagList tags={row.tags} />
        </Table.Cell>
        <Table.Cell mono muted className="text-[11px]">
          {row.source}
        </Table.Cell>
        <Table.Cell mono muted className="text-[11px]">
          {row.sourceThreadId ? (
            <Link
              to={assistantThreadDeepLinkPath({
                userId: row.userId ?? undefined,
                familyId: row.familyId,
                threadId: row.sourceThreadId,
              })}
              className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
              title={row.sourceThreadId}
            >
              {row.sourceThreadId.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-fg-quaternary">—</span>
          )}
        </Table.Cell>
        <Table.Cell mono muted title={row.userId ?? undefined} className="text-[11px]">
          {writer === null ? <span className="text-fg-quaternary">—</span> : <InspectLinkedText text={writer} />}
        </Table.Cell>
        <Table.Cell mono muted className="text-[11px]">
          {formatTimeAgo(row.updatedAt)}
        </Table.Cell>
        <Table.Cell mono muted className="text-[11px]" title={`${new Date(row.createdAt).toISOString()} (UTC)`}>
          {formatCreatedPacific(row.createdAt)}
        </Table.Cell>
        <Table.Cell mono muted className="text-[11px]" title={row.id}>
          <div className="flex items-center gap-1">
            {/* Full id in the DOM, clipped by CSS — `select-all` must still yield the
                whole uuid, which is the fallback when a clipboard write is refused. */}
            <span className="w-16 select-all truncate">{row.id}</span>
            <CopyButton value={row.id} label="Copy memory id" successMessage="Memory id copied." />
            <IconButton label="Memory history" onClick={() => setRowHistory(row)}>
              <ClockIcon size={14} aria-hidden />
            </IconButton>
            <IconButton
              label="Delete memory"
              tone="danger"
              disabled={actionLoading}
              onClick={() => {
                setRowDelete(row);
                setRowReason('');
              }}
            >
              <TrashIcon size={14} aria-hidden />
            </IconButton>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const handleRowDeleteConfirm = async () => {
    if (!rowDelete || rowReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      await deleteRow({
        variables: {
          id: rowDelete.id,
          scope: rowDelete.scope,
          reason: prefixReason(rowReason, mopsUser?.email ?? null),
        },
      });
      showToast({ type: 'success', message: 'Memory deleted.' });
      setRowDelete(null);
      setRowReason('');
      await refetchMemory();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (!userId || bulkReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      const result = await deleteAll({
        variables: { userId, reason: prefixReason(bulkReason, mopsUser?.email ?? null) },
      });
      const count = result.data?.adminDeleteAllAssistantMemoryForUser ?? 0;
      showToast({
        type: 'success',
        message: count === 0 ? 'No memories to delete.' : `Deleted ${count} memories.`,
      });
      setBulkDeleteOpen(false);
      setBulkReason('');
      await refetchMemory();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  return (
    // Owns its own scrolling: the Assistant shell is pinned to the viewport.
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <div className="w-full max-w-md">
        <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
      </div>

      {!userId ? (
        <div className="text-fg-tertiary text-xs italic">Select a user to view assistant memories.</div>
      ) : memoryLoading ? (
        <div className="text-fg-secondary text-xs">Loading memories…</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-fg-secondary text-xs">
              {selectedUserEmail ? (
                <>
                  Memories for {includesFamilyScope && 'family of '}
                  <span className="font-mono">{selectedUserEmail}</span>
                </>
              ) : (
                'Memories'
              )}
              {memories.length > 0 && <span className="text-fg-tertiary"> · {memories.length} active</span>}
            </div>
            <Button
              color="danger"
              size="sm"
              isDisabled={memories.length === 0 || actionLoading}
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete all
            </Button>
          </div>

          {hasMore && (
            <div className="border-fg-warning-primary text-fg-warning-primary bg-bg-secondary border-l-2 px-3 py-2 text-xs">
              Showing 500 most recent memories per scope. Older rows hidden.
            </div>
          )}

          {memories.length === 0 ? (
            <div className="text-fg-tertiary text-xs italic">
              No memories recorded for this {includesFamilyScope ? 'family' : 'user'}.
            </div>
          ) : (
            <Table>
              <Table.Header sticky>
                <Table.Row>
                  {COLUMNS.map((col) => (
                    <Table.HeaderCell
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={cx(
                        'hover:text-fg-primary cursor-pointer select-none',
                        col.align === 'right' && 'text-right'
                      )}
                    >
                      <span className="inline-flex items-baseline gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="text-fg-quaternary text-[10px]">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </span>
                    </Table.HeaderCell>
                  ))}
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              {preferenceMemories.length > 0 && (
                <Table.Body>
                  <MemorySectionLabel label="Preferences" count={preferenceMemories.length} />
                  {preferenceMemories.map(renderMemoryRow)}
                </Table.Body>
              )}
              {otherMemories.length > 0 && (
                <Table.Body>
                  {preferenceMemories.length > 0 && (
                    <MemorySectionLabel label="Memories" count={otherMemories.length} />
                  )}
                  {otherMemories.map(renderMemoryRow)}
                </Table.Body>
              )}
            </Table>
          )}
        </div>
      )}

      <Modal isOpen={rowHistory !== null} onOpenChange={(open) => !open && setRowHistory(null)} size="lg">
        <Modal.Header onClose={() => setRowHistory(null)}>Memory history</Modal.Header>
        <Modal.Body>
          <MemoryHistoryBody userId={userId} row={rowHistory} />
        </Modal.Body>
      </Modal>

      <Modal isOpen={rowDelete !== null} onOpenChange={(open) => !open && setRowDelete(null)} size="md">
        <Modal.Header onClose={() => setRowDelete(null)}>Delete memory</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              This <span className="font-semibold">hard-deletes</span> the memory — the row is dropped from the DB and
              cannot be recovered. A reason is required and logged to CloudWatch.
            </div>
            {rowDelete && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
                <div className="font-mono text-[11px]">{rowDelete.body}</div>
              </div>
            )}
            <Input
              label="Reason"
              placeholder="e.g. user requested permanent removal, duplicate, model hallucination"
              value={rowReason}
              onChange={setRowReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setRowDelete(null)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={rowReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleRowDeleteConfirm()}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={bulkDeleteOpen} onOpenChange={(open) => !open && setBulkDeleteOpen(false)} size="md">
        <Modal.Header onClose={() => setBulkDeleteOpen(false)}>Delete all memories</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              This <span className="font-semibold">hard-deletes every</span> memory for this{' '}
              {includesFamilyScope ? 'family across both user and family scopes' : 'user'} (including
              already-invalidated rows). Rows cannot be recovered. A reason is required and logged to CloudWatch.
            </div>
            <Input
              label="Reason"
              placeholder="e.g. user requested permanent removal, ops cleanup"
              value={bulkReason}
              onChange={setBulkReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setBulkDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={bulkReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleBulkDeleteConfirm()}
              >
                Delete all
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

interface IMemoryHistoryBodyProps {
  userId: string | null;
  row: MemoryRow | null;
}

/**
 * Split out so the query unmounts with the modal: keeping the hook in the scene
 * would hold the last-opened row's entries after it closed, and the next open
 * would paint them under a different row's heading.
 */
const MemoryHistoryBody = ({ userId, row }: IMemoryHistoryBodyProps) => {
  const { entries, loading, hasMore, error } = useAssistantMemoryJournal({ userId, memoryId: row?.id ?? null });
  if (!row) {
    return null;
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
        <div className="font-mono text-[11px]">
          <MemoryBodyText body={row.body} />
        </div>
      </div>
      <AssistantMemoryJournalList
        entries={entries}
        loading={loading}
        hasMore={hasMore}
        error={error}
        threadLinkContext={{ userId: row.userId ?? userId, familyId: row.familyId }}
      />
    </div>
  );
};

// Operators are SF-based; pin Created to Pacific so the column reads in
// one zone regardless of the operator's browser. `sv-SE` yields the dense
// `YYYY-MM-DD HH:MM:SS` shape the column already used.
const PACIFIC_CREATED_FORMATTER = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatCreatedPacific(createdAt: string): string {
  return PACIFIC_CREATED_FORMATTER.format(new Date(createdAt));
}

interface IMemorySectionLabelProps {
  label: string;
  count: number;
}

const MemorySectionLabel = ({ label, count }: IMemorySectionLabelProps) => (
  <Table.Row className="bg-bg-secondary">
    <Table.Cell
      colSpan={COLUMNS.length + 1}
      className="text-fg-tertiary py-1 text-[10px] font-medium uppercase tracking-wider"
    >
      {label} · {count}
    </Table.Cell>
  </Table.Row>
);

// `min-w-0 truncate` lets each chip shrink and ellipsize when the row
// overflows the fixed-width cell, rather than the last chip getting
// hard-clipped by the container's `overflow-hidden`.
const TAG_CHIP_BASE = 'min-w-0 truncate border px-1.5 py-0.5 text-[10px] font-medium tracking-wide';
const SYS_TAG_CHIP = 'bg-bg-secondary text-fg-brand-primary border-border-brand';
const TOPIC_TAG_CHIP = 'bg-bg-secondary text-fg-secondary border-border-primary';

interface ITagListProps {
  tags: string[];
}

const TagList = ({ tags }: ITagListProps) => {
  if (tags.length === 0) {
    return <span className="text-fg-quaternary text-[10px]">—</span>;
  }
  const sorted = [...tags].sort((a, b) => a.localeCompare(b));
  return (
    <div className="flex w-[200px] gap-1 overflow-hidden">
      {sorted.map((tag) => (
        <span
          key={tag}
          title={tag}
          className={cx(TAG_CHIP_BASE, tag.startsWith(RESERVED_TAG_PREFIX) ? SYS_TAG_CHIP : TOPIC_TAG_CHIP)}
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

/**
 * How a row's writer reads in the pane: their email where the household still
 * has the account, else the bare id, else null — a household row keeps its body
 * after the member who last wrote it is deleted.
 */
function writerLabel(row: MemoryRow, userEmailById: Map<string, string>): string | null {
  const writerId = row.userId ?? null;
  if (writerId === null) {
    return null;
  }
  return userEmailById.get(writerId) ?? writerId;
}

function compareRows(a: MemoryRow, b: MemoryRow, key: SortKey, userEmailById: Map<string, string>): number {
  switch (key) {
    case 'scope':
      return a.scope.localeCompare(b.scope);
    case 'importance':
      return a.importance - b.importance;
    case 'effectiveImportance':
      return a.effectiveImportance - b.effectiveImportance;
    case 'body':
      return a.body.localeCompare(b.body);
    case 'tags':
      // Sort each set first so identical tags in different insertion order compare equal.
      return [...a.tags]
        .sort()
        .join(',')
        .localeCompare([...b.tags].sort().join(','));
    case 'source':
      return a.source.localeCompare(b.source);
    case 'sourceThread':
      return (a.sourceThreadId ?? '').localeCompare(b.sourceThreadId ?? '');
    case 'user': {
      // A row whose writer's account is gone sorts with the blanks, not under
      // whatever its id happened to be.
      return (writerLabel(a, userEmailById) ?? '').localeCompare(writerLabel(b, userEmailById) ?? '');
    }
    case 'updatedAt':
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    case 'createdAt':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    default:
      return 0;
  }
}

export default AssistantMemoryScene;
